import { type FC, useMemo } from "react";
import { Button, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import { DialogHeader } from "@/shared/components";
import { useUser } from "@/partner/entities/auth";
import { useLiveEnabled } from "@/partner/entities/instance";
import { type LinkFormValues, linkRewardFormErrors } from "../model";
import { LinkTypeType } from "@/types/graphql/partner.generated";
import { getLinkSchema } from "../model/schemas";

const FORM_ID = "link-form";

interface LinkFormProps {
  title: string;
  loading?: boolean;
  initialValues?: LinkFormValues;
  onSubmit: (values: LinkFormValues) => Promise<void>;
  onClose: () => void;
}

export const LinkForm: FC<LinkFormProps> = ({
  title,
  initialValues = {
    linkType: LinkTypeType.InternalType,
    rewardType: "NONE",
  },
  loading = false,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const currency = user.currentOrganization?.currency;
  const liveEnabled = useLiveEnabled();

  const schema = useMemo(
    () => getLinkSchema(t, undefined, currency, liveEnabled),
    [t, currency, liveEnabled]
  );
  const validate = useMemo(
    () => (values: unknown) =>
      linkRewardFormErrors(values as Partial<LinkFormValues>, t),
    [t]
  );

  const handleSubmit = async (values: Record<string, unknown>) => {
    await onSubmit(values as unknown as LinkFormValues);
  };

  return (
    <>
      <DialogHeader
        title={title}
        actions={
          <Button
            variant="contained"
            type="submit"
            form={FORM_ID}
            disabled={loading}
          >
            {t("common.actions.save")}
          </Button>
        }
        onClose={onClose}
        loading={loading}
      />

      <DialogContent sx={{ p: 1 }}>
        <FormBuilder
          formId={FORM_ID}
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          submitButton={false}
          resetButton={false}
          disabled={loading}
          paper
        />
      </DialogContent>
    </>
  );
};
